import { useState } from "react";
import { useDispatch } from "react-redux";
import { parseResumeVersion, applyParsedResume } from "../slice/candidateSlice";

// One shared "Parse & Fill Profile" action, used by both the Candidate
// Detail page and the Edit Candidate page so the two can never drift apart.
//
// What it does, in order:
//   1. If the resume version has no extracted text, explains why it can't
//      be parsed and stops.
//   2. If the version hasn't been AI-parsed yet, parses it (LLM Op #1).
//      If it's already parsed, this step is skipped — no second AI call.
//   3. Applies the parsed data to the candidate profile. By default it only
//      fills blank fields; overwrite: true replaces existing values.
//   4. If the resume's name doesn't look like the candidate's name, asks
//      for confirmation before writing anything.
//
// Returns true only when the profile was actually updated.
export default function useParseAndFill({ candidateId, notify, confirm }) {
  const dispatch = useDispatch();
  const [workingVersionId, setWorkingVersionId] = useState(null);

  const runParseAndFill = async (version, { overwrite = false } = {}) => {
    if (!version) return false;

    if (!version.hasExtractedText) {
      await notify(
        "This resume version has no extracted text (the file may be a scanned/image-only PDF, or extraction failed at upload time). Re-upload it as a text-based PDF or DOCX to use AI parsing.",
        { title: "Can't parse this resume" }
      );
      return false;
    }

    setWorkingVersionId(version.id);
    try {
      // Step 1: parse only if needed.
      if (!version.parsedData) {
        const parseResult = await dispatch(parseResumeVersion({ candidateId, versionId: version.id }));
        if (parseResumeVersion.rejected.match(parseResult)) {
          await notify(`AI parsing failed: ${parseResult.payload || "Unknown error."}`, { title: "Parsing failed" });
          return false;
        }
      }

      // Step 2: fill the profile.
      let applyResult = await dispatch(applyParsedResume({ id: candidateId, versionId: version.id, overwrite }));
      if (applyParsedResume.rejected.match(applyResult)) {
        await notify(`Could not fill the profile: ${applyResult.payload || "Unknown error."}`, { title: "Fill failed" });
        return false;
      }

      // Step 3: name mismatch safety check (nothing was written yet).
      if (applyResult.payload.needsConfirmation) {
        const proceed = await confirm(applyResult.payload.nameMismatchWarning, {
          title: "Name mismatch",
          confirmLabel: "Fill anyway",
        });
        if (!proceed) return false;

        applyResult = await dispatch(
          applyParsedResume({ id: candidateId, versionId: version.id, overwrite, confirmMismatch: true })
        );
        if (applyParsedResume.rejected.match(applyResult)) {
          await notify(`Could not fill the profile: ${applyResult.payload || "Unknown error."}`, { title: "Fill failed" });
          return false;
        }
      }

      return true;
    } finally {
      setWorkingVersionId(null);
    }
  };

  return { runParseAndFill, workingVersionId };
}
